import { formatNumber, type NewsletterSummaryDto } from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, Mails } from 'lucide-react';
import { GlassPanel } from '../../components/GlassPanel.js';
import { api } from '../../lib/api.js';

/**
 * The make.as newsletter connection: whether Nova can read the lists and pull
 * statistics (07 — statistikk). Read-only in v1 — sending stays in make.as. The
 * lists shown here are the ones that appear in the send-time picker.
 */
export function NewsletterAdmin() {
  const { data } = useQuery({
    queryKey: ['newsletter-summary'],
    queryFn: () => api.get<NewsletterSummaryDto>('/newsletter/summary'),
  });

  if (!data) return null;

  return (
    <GlassPanel data-testid="newsletter-admin">
      <div className="flex items-center gap-2">
        <Mails size={14} className="text-ink-soft" aria-hidden />
        <h2 className="font-display text-ink text-base">Nyhetsbrev (make.as)</h2>
      </div>

      {data.configured ? (
        <p className="text-success mt-2 flex items-center gap-1.5 text-xs">
          <Check size={13} aria-hidden />
          Koblet til — {formatNumber(data.lists.length)} lister,{' '}
          {formatNumber(data.totalSubscribers)} abonnenter
        </p>
      ) : (
        <p className="text-warning mt-2 flex items-start gap-1.5 text-xs leading-relaxed">
          <AlertTriangle size={13} className="mt-0.5 shrink-0" aria-hidden />
          <span>
            Ikke koblet på. Mangler <code>MAKE_API_USERID</code> og <code>MAKE_API_KEY</code> i{' '}
            <code>.env</code>. Statistikken hentes så snart nøkkelen er på plass.
          </span>
        </p>
      )}

      {data.configured && (
        <>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <Stat label="Abonnenter" value={formatNumber(data.totalSubscribers)} />
            <Stat
              label="Åpningsrate"
              value={data.averageOpenRate === null ? '–' : `${data.averageOpenRate} %`}
            />
            <Stat label="Utsendinger" value={formatNumber(data.newsletterCount)} />
          </div>

          <p className="label-micro mt-4 mb-1.5">
            Lister — velges ved utsending (én eller flere)
          </p>
          <div className="space-y-1.5">
            {data.lists.length === 0 && (
              <p className="text-ink-soft text-[11px]">
                Ingen lister ennå. Opprett segmentlistene (kommune, stat, bank …) i make.as.
              </p>
            )}
            {data.lists.map((list) => (
              <div
                key={list.id}
                className="border-hairline bg-base/30 flex items-center justify-between gap-2 rounded-lg border px-2.5 py-1.5"
              >
                <span className="text-ink truncate text-xs">{list.title}</span>
                <span className="text-ink-soft shrink-0 text-[11px]">
                  {formatNumber(list.subscriberEmails)} e-post
                </span>
              </div>
            ))}
          </div>

          <p className="text-ink-soft mt-3 text-[11px] leading-relaxed">
            Nova leser lister og statistikk herfra — selve utsendingen skjer i make.as.
            Analytikeren skriver abonnenttall og åpningsrate til KPI-ene automatisk.
          </p>
        </>
      )}
    </GlassPanel>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-hairline bg-base/30 rounded-lg border px-2 py-1.5 text-center">
      <p className="num-tabular font-display text-ink text-sm font-bold">{value}</p>
      <p className="label-micro">{label}</p>
    </div>
  );
}
